import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getApiV1ClassroomdisciplineGradeAndFrequenceClassStudentsClassstudentidQueryKey,
  useDeleteApiV1ClassroomdisciplineGradeAndFrequenceClassModulesClassmoduleid,
  usePatchApiV1ClassroomdisciplineGradeAndFrequenceClassModulesClassmoduleidDisciplinesDisciplineidIncludedInAttendance,
  usePatchApiV1ClassroomdisciplineGradeAndFrequenceClassModulesClassmoduleidDisciplinesDisciplineidIncludedInGrade,
  usePatchApiV1ClassroomdisciplineGradeAndFrequenceClassModulesClassmoduleidDisciplinesDisciplineidShowOnStudentDocument,
} from '@/shared/api/generated';

import {
  type FilterModule,
  gradeConfigStateAtom,
  gradeExpandedAtom,
  gradeSimulatorAtom,
} from '../store/atoms/gradeConfigAtom';
import { type Activity, type ClassGroup, EActivityType, type Module } from '../types/gradeConfig';
import { getSource } from '../utils/gradeCalc';
import { nextId } from '../utils/nextId';

/**
 * Controle de documento alternável por disciplina vinculada (mapeia para os três PATCH
 * `grade-and-frequence/class-modules/{classModuleId}/disciplines/{disciplineId}/*`).
 */
export type EDocumentControl = 'document' | 'grade' | 'attendance';

/** `true` quando `value` é um id numérico real (backend), não sintético (`disc_x_y`). */
function isRealId(value: string | null | undefined): value is string {
  return !!value && /^\d+$/.test(value);
}

/** Monta uma nova atividade do tipo informado, escolhendo módulo e item default. */
function buildActivity(
  classGroup: ClassGroup,
  type: EActivityType,
  filterModule: FilterModule,
): Activity {
  let moduleId: string | null = null;
  if (filterModule !== 'all' && filterModule !== 'none') moduleId = filterModule;
  else if (classGroup.modules.length) moduleId = classGroup.modules[0]?.id ?? null;

  const src = getSource(classGroup, type);
  const used = classGroup.activities.filter(a => a.type === type).map(a => a.refId);
  const available = src.find(x => !used.includes(x.id));
  const refId = available?.id ?? src[0]?.id ?? null;

  let closing: Activity['closing'] = type === EActivityType.Discipline ? 'disciplina' : 'curso';
  if (type === EActivityType.Discipline) {
    const d = src.find(x => x.id === refId);
    closing = d?.lessons?.length === 1 ? 'curso' : 'disciplina';
  }

  return {
    id: nextId('a'),
    type,
    refId,
    moduleId,
    showInDocument: true,
    countsInGrade: true,
    countsInAttendance: type === EActivityType.Discipline,
    attendanceByView: type === EActivityType.Discipline,
    closing,
    moduleWeight: 20,
    defaultGrade: null,
    defaultAttendance: null,
  };
}

/**
 * Hook orquestrador da turma ativa: expõe módulos, vínculos e as ações de edição
 * (atividades, pesos por avaliação, toggles de controle) e a seleção de turma.
 *
 * As alterações são persistidas por interação (vínculo/desvínculo via mutations); não há
 * ações de "salvar"/"resetar" em lote. Encapsula o `gradeConfigStateAtom` — os componentes
 * nunca acessam o átomo diretamente.
 *
 * @example
 * ```tsx
 * const { classGroup, modules, updateActivity } = useGradeConfig();
 * ```
 */
export function useGradeConfig() {
  const { t } = useTranslation('gradeAttendanceConfig');
  const { alert } = useAlert();
  const [state, setState] = useAtom(gradeConfigStateAtom);
  const setSimulator = useSetAtom(gradeSimulatorAtom);
  const [expanded, setExpanded] = useAtom(gradeExpandedAtom);
  const queryClient = useQueryClient();
  const unlinkMutation =
    useDeleteApiV1ClassroomdisciplineGradeAndFrequenceClassModulesClassmoduleid();
  const showOnDocumentMutation =
    usePatchApiV1ClassroomdisciplineGradeAndFrequenceClassModulesClassmoduleidDisciplinesDisciplineidShowOnStudentDocument();
  const includedInGradeMutation =
    usePatchApiV1ClassroomdisciplineGradeAndFrequenceClassModulesClassmoduleidDisciplinesDisciplineidIncludedInGrade();
  const includedInAttendanceMutation =
    usePatchApiV1ClassroomdisciplineGradeAndFrequenceClassModulesClassmoduleidDisciplinesDisciplineidIncludedInAttendance();

  const classGroup = useMemo(
    () => state.classGroups.find(c => c.id === state.selectedClassId),
    [state.classGroups, state.selectedClassId],
  );

  /** Substitui a turma ativa aplicando um updater imutável. */
  const setActiveClass = useCallback(
    (updater: (c: ClassGroup) => ClassGroup) => {
      setState(prev => {
        const id = prev.selectedClassId || prev.classGroups[0]?.id;
        return {
          ...prev,
          classGroups: prev.classGroups.map(c => (c.id === id ? updater(c) : c)),
        };
      });
    },
    [setState],
  );

  // ── Filtros ────────────────────────────────────────────────────────────────
  const setFilterModule = useCallback(
    (filterModule: FilterModule) => setState(prev => ({ ...prev, filterModule })),
    [setState],
  );

  // ── Atividades (vínculos) ────────────────────────────────────────────────────
  const updateActivity = useCallback(
    (id: string, patch: Partial<Activity>) => {
      setActiveClass(c => ({
        ...c,
        activities: c.activities.map(a => (a.id === id ? { ...a, ...patch } : a)),
      }));
    },
    [setActiveClass],
  );

  /**
   * Alterna um controle de documento de uma disciplina vinculada. Atualiza a working copy de
   * forma otimista e persiste via PATCH (`show-on-student-document`/`included-in-grade`/
   * `included-in-attendance`) — endpoints sem corpo que apenas invertem a flag no servidor.
   * Dispara um alert de sucesso ou de erro (revertendo a working copy em caso de falha).
   *
   * O PATCH só é chamado quando `moduleId` e `refId` são ids reais (numéricos). O GET agora
   * expõe `disciplineId`, então disciplinas carregadas persistem; só atividades novas ainda
   * sem disciplina selecionada (refId nulo/sintético) alternam apenas localmente.
   */
  const toggleControl = useCallback(
    (activity: Activity, control: EDocumentControl) => {
      const patch: Partial<Activity> =
        control === 'document'
          ? { showInDocument: !activity.showInDocument }
          : control === 'grade'
            ? { countsInGrade: !activity.countsInGrade }
            : { countsInAttendance: !activity.countsInAttendance };
      const revert: Partial<Activity> =
        control === 'document'
          ? { showInDocument: activity.showInDocument }
          : control === 'grade'
            ? { countsInGrade: activity.countsInGrade }
            : { countsInAttendance: activity.countsInAttendance };

      updateActivity(activity.id, patch); // otimista

      // Sem ids reais (atividade nova ainda sem disciplina) não há como persistir.
      if (!isRealId(activity.moduleId) || !isRealId(activity.refId)) return;

      const vars = {
        classModuleId: Number(activity.moduleId),
        disciplineId: Number(activity.refId),
      };
      const handlers = {
        onSuccess: () => alert({ alertType: 'success', title: t('controls.toast.success') }),
        onError: () => {
          updateActivity(activity.id, revert);
          alert({ alertType: 'warning', title: t('controls.toast.error') });
        },
      };
      if (control === 'document') showOnDocumentMutation.mutate(vars, handlers);
      else if (control === 'grade') includedInGradeMutation.mutate(vars, handlers);
      else includedInAttendanceMutation.mutate(vars, handlers);
    },
    [
      updateActivity,
      alert,
      t,
      showOnDocumentMutation,
      includedInGradeMutation,
      includedInAttendanceMutation,
    ],
  );

  const changeType = useCallback(
    (id: string, type: EActivityType) => {
      setActiveClass(c => ({
        ...c,
        activities: c.activities.map(a => {
          if (a.id !== id) return a;
          const fresh = buildActivity(c, type, state.filterModule);
          return {
            ...a,
            type,
            refId: fresh.refId,
            countsInAttendance: type === EActivityType.Discipline ? a.countsInAttendance : false,
            attendanceByView: type === EActivityType.Discipline,
          };
        }),
      }));
    },
    [setActiveClass, state.filterModule],
  );

  const removeActivity = useCallback(
    (id: string) => {
      // Desvincula no servidor ("Desanexar Aulas do Módulo") enviando os ids das AULAS
      // (`lessons`) reais da disciplina — não o id da disciplina. Só ocorre quando o módulo é
      // real e há ao menos uma aula com id numérico; aulas com id sintético (backend sem id)
      // são removidas apenas localmente.
      const at = classGroup?.activities.find(a => a.id === id);
      const disc = at ? classGroup?.disciplines.find(d => d.id === at.refId) : undefined;
      const aulaIds = (disc?.lessons ?? []).filter(l => /^\d+$/.test(l.id)).map(l => Number(l.id));
      if (at?.moduleId && /^\d+$/.test(at.moduleId) && aulaIds.length > 0) {
        unlinkMutation.mutate(
          {
            classModuleId: Number(at.moduleId),
            data: { classroomDisciplineIds: aulaIds },
          },
          {
            onSuccess: () => {
              if (state.selectedClassId) {
                queryClient.invalidateQueries({
                  queryKey:
                    getApiV1ClassroomdisciplineGradeAndFrequenceClassStudentsClassstudentidQueryKey(
                      Number(state.selectedClassId),
                    ),
                });
              }
            },
          },
        );
      }
      setActiveClass(c => ({ ...c, activities: c.activities.filter(a => a.id !== id) }));
    },
    [classGroup, unlinkMutation, queryClient, state.selectedClassId, setActiveClass],
  );

  const toggleExpand = useCallback(
    (id: string) => setExpanded(prev => ({ ...prev, [id]: !prev[id] })),
    [setExpanded],
  );

  // ── Ações de página ────────────────────────────────────────────────────────
  const selectClass = useCallback(
    (id: string, name = '') => {
      setState(prev => ({
        ...prev,
        selectedClassId: id,
        selectedClassName: name,
        // Limpa a working copy para o loader semear a nova turma (ou zerar ao desselecionar).
        classGroups: [],
        filterModule: 'all',
      }));
      setSimulator({ grades: {}, attendance: {} });
      setExpanded({});
    },
    [setState, setSimulator, setExpanded],
  );

  return {
    classGroup,
    /** `true` quando há uma turma selecionada (mesmo que ainda carregando). */
    hasSelection: state.selectedClassId !== '',
    modules: (classGroup?.modules ?? []) as readonly Module[],
    activities: classGroup?.activities ?? [],
    disciplines: classGroup?.disciplines ?? [],
    filterModule: state.filterModule,
    expanded,
    toggleExpand,
    setFilterModule,
    updateActivity,
    toggleControl,
    changeType,
    removeActivity,
    selectClass,
  } as const;
}
